import React, { useRef, useEffect } from "react";
/**
 * Renders the signature HTML inside a sandboxed iframe using srcdoc.
 *
 * WHY IFRAME (not dangerouslySetInnerHTML)?
 * 1. Style isolation: the signature uses inline styles and table layout.
 *    Rendering it in the task pane DOM would inherit/conflict with the
 *    add-in's own CSS (e.g., box-sizing, font resets).
 * 2. Security: srcdoc prevents the signature HTML from executing scripts
 *    or accessing the parent window's DOM.
 * 3. Fidelity: the iframe provides an environment closer to how an email
 *    client renders the HTML — no external stylesheets, reset CSS, etc.
 */
export default function SignaturePreview({ html }) {
    const iframeRef = useRef(null);
    const [height, setHeight] = React.useState(180);
    // Auto-size the iframe to the content height so the task pane
    // doesn't show unnecessary scrollbars inside the preview.
    useEffect(() => {
        const iframe = iframeRef.current;
        if (!iframe)
            return;
        function onLoad() {
            var _a;
            try {
                const body = (_a = iframe === null || iframe === void 0 ? void 0 : iframe.contentDocument) === null || _a === void 0 ? void 0 : _a.body;
                if (body) {
                    const h = body.scrollHeight;
                    if (h > 0)
                        setHeight(h + 16);
                }
            }
            catch (_b) {
                // cross-origin safety — should not occur with srcdoc
            }
        }
        iframe.addEventListener("load", onLoad);
        return () => iframe.removeEventListener("load", onLoad);
    }, [html]);
    const srcdoc = `<!DOCTYPE html>
<html>
<head>
<meta charset="UTF-8"/>
<style>
  body { margin: 0; padding: 8px; background: #ffffff; }
</style>
</head>
<body>
${html}
</body>
</html>`;
    return (React.createElement("div", { style: styles.wrapper },
        React.createElement("iframe", { ref: iframeRef, srcDoc: srcdoc, sandbox: "allow-same-origin", title: "Signature preview", style: Object.assign(Object.assign({}, styles.iframe), { height }), scrolling: "no" })));
}
const styles = {
    wrapper: {
        border: "1px solid #d0d0d0",
        borderRadius: 4,
        overflow: "hidden",
        backgroundColor: "#ffffff",
    },
    iframe: {
        width: "100%",
        border: "none",
        display: "block",
    },
};
